import { createRoot } from 'react-dom/client';
import './index.css';
import App, { store } from './App';
import * as serviceWorker from './serviceWorker';
import './custom.scss';
import 'bootstrap/dist/js/bootstrap.bundle.min';
import {
    initializeFirebase,
    setupForegroundMessageHandler,
    validateAndRefreshToken,
    onTokenRefresh,
    isMessagingAvailable
} from './push-notification';
import { setToast } from './Redux/OverlayActions';

// Set viewport height for mobile browsers
const setViewportHeight = () => {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
};
window.addEventListener('resize', setViewportHeight);
setViewportHeight();

// Render the app
const container = document.getElementById('root');
const root = createRoot(container);
root.render(<App />);

// Initialize Firebase
const firebaseInitialized = initializeFirebase();

if (firebaseInitialized) {
    // Setup foreground message handler
    setupForegroundMessageHandler((payload) => {
        const notification = payload.notification;
        if (notification) {
            store.dispatch(setToast({
                title: notification.title || '',
                message: notification.body || 'Nieuw bericht ontvangen'
            }));
        }
    });

    // Register token refresh callback to update backend
    onTokenRefresh(async (newToken) => {
        console.info('[App] FCM token refreshed, updating backend');
        await updateTokenOnBackend(newToken);
    });

    // Listen for push subscription changes from service worker
    if ('serviceWorker' in navigator) {
        navigator.serviceWorker.addEventListener('message', (event) => {
            if (event.data?.type === 'PUSH_SUBSCRIPTION_CHANGED') {
                console.info('[App] Push subscription changed, refreshing token');
                validateAndRefreshToken(updateTokenOnBackend);
            }
        });
    }

    // Validate and refresh token on app startup (if user has active notifications)
    const silentCallActive = localStorage.getItem('silentCallActive') === '1';
    const hasExistingToken = localStorage.getItem('fcm_token') || localStorage.getItem('token');

    if (silentCallActive && hasExistingToken && isMessagingAvailable()) {
        // Only refresh if user has notifications enabled and permission granted
        if (Notification.permission === 'granted') {
            // Delay token refresh to not block app startup
            setTimeout(() => {
                validateAndRefreshToken(updateTokenOnBackend)
                    .then(result => {
                        if (result.refreshed) {
                            console.info('[App] Token validated and refreshed on startup');
                        }
                    })
                    .catch(err => {
                        console.warn('[App] Token refresh failed on startup:', err);
                    });
            }, 3000);
        }
    }
}

/**
 * Update FCM token on the backend
 * This is called when a new token is obtained or the token is refreshed
 */
async function updateTokenOnBackend(newToken) {
    const udid = localStorage.getItem('udid');
    const oldToken = localStorage.getItem('token');

    // Skip if no udid (user hasn't registered yet)
    if (!udid) {
        console.info('[App] No udid, skipping backend token update');
        return;
    }

    // Skip if token hasn't changed
    if (oldToken === newToken) {
        console.info('[App] Token unchanged, skipping backend update');
        return;
    }

    try {
        const response = await fetch(
            process.env.REACT_APP_ICECORP_API_HREF + 'v1/fcmtoken/refresh',
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    company_id: parseInt(process.env.REACT_APP_COMPANY_ID, 10),
                    old_token: oldToken || '',
                    new_token: newToken,
                    udid: udid,
                    version_info: window.versionInfo || '',
                }),
            }
        );

        const data = await response.json();

        if (data.success) {
            // Update stored token
            localStorage.setItem('token', newToken);
            console.info('[App] Backend token updated successfully, updated:', data.updated_count);
        } else {
            console.warn('[App] Backend token update failed:', data);
        }
    } catch (error) {
        console.error('[App] Error updating token on backend:', error);
    }
}

// Register service worker for PWA functionality. REACT_APP_DISABLE_SW=true
// (used for the tenant test builds on dev) actively unregisters any existing
// service worker instead, so a stale cache can't keep serving an old build.
if (process.env.REACT_APP_DISABLE_SW === 'true') {
    serviceWorker.unregister();
} else {
    serviceWorker.register();
}
